<template>
  <div class="wrapper wrapper-content animated fadeInRight">
    <div class="row">
      <div class="col-lg-3">
        <analytics-unit-block
          :period="selectedDate | moment('h:00 a')"
          :unit="'Hour'"
          :unitClass="'label-success'"
          :description="'Received'"
          :selected="selectedPeriod === 'hour'"
          :received="chartsData['hour-stats'].total"
          :growth="chartsData['hour-stats'].growth"
          :stats="chartsData['hour-stats'].points"
          @previous="$emit('previous', { unit: 'hour' })"
          @next="$emit('next', { unit: 'hour' })"
          @click.native="$emit('changePeriod', { unit: 'hour' })"
        >
          <content-loader-placeholder
            v-if="!Boolean(chartsData['hour-stats'].points.length)"
            :type="'AnalyticsUnitBlock'"
            slot="content-loader"
          ></content-loader-placeholder>
        </analytics-unit-block>
      </div>
      <div class="col-lg-3">
        <analytics-unit-block
          :period="selectedDate | moment('dddd Do')"
          :unit="'Day'"
          :unitClass="'label-info'"
          :description="'Received'"
          :selected="selectedPeriod === 'day'"
          :received="chartsData['day-stats'].total"
          :growth="chartsData['day-stats'].growth"
          :stats="chartsData['day-stats'].points"
          @previous="$emit('previous', { unit: 'day' })"
          @next="$emit('next', { unit: 'day' })"
          @click.native="$emit('changePeriod', { unit: 'day' })"
        >
          <content-loader-placeholder
            v-if="!Boolean(chartsData['day-stats'].points.length)"
            :type="'AnalyticsUnitBlock'"
            slot="content-loader"
          ></content-loader-placeholder>
        </analytics-unit-block>
      </div>
      <div class="col-lg-3">
        <analytics-unit-block
          :period="selectedDate | moment('MMMM')"
          :unit="'Month'"
          :unitClass="'label-primary'"
          :description="'Received'"
          :selected="selectedPeriod === 'month'"
          :received="chartsData['month-stats'].total"
          :growth="chartsData['month-stats'].growth"
          :stats="chartsData['month-stats'].points"
          @previous="$emit('previous', { unit: 'month' })"
          @next="$emit('next', { unit: 'month' })"
          @click.native="$emit('changePeriod', { unit: 'month' })"
        >
          <content-loader-placeholder
            v-if="!Boolean(chartsData['month-stats'].points.length)"
            :type="'AnalyticsUnitBlock'"
            slot="content-loader"
          ></content-loader-placeholder>
        </analytics-unit-block>
      </div>
      <div class="col-lg-3">
        <analytics-unit-block
          :period="selectedDate | moment('YYYY')"
          :unit="'Year'"
          :unitClass="'label-danger'"
          :description="'Received'"
          :selected="selectedPeriod === 'year'"
          :received="chartsData['year-stats'].total"
          :growth="chartsData['year-stats'].growth"
          :stats="chartsData['year-stats'].points"
          @previous="$emit('previous', { unit: 'year' })"
          @next="$emit('next', { unit: 'year' })"
          @click.native="$emit('changePeriod', { unit: 'year' })"
        >
          <content-loader-placeholder
            v-if="!Boolean(chartsData['year-stats'].points.length)"
            :type="'AnalyticsUnitBlock'"
            slot="content-loader"
          ></content-loader-placeholder>
        </analytics-unit-block>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-3">
        <i-box :contentHeight="235">
          <span slot="title">
            <h5>Total vs unique</h5>
          </span>
          <span slot="content">
            <analytics-chart
              v-if="Boolean(chartsData['unique-stats']['total-vs-unique'].length)"
              type="bar"
              chartStyle="max-height: 200px;"
              :barPercentage="0.9"
              :categoryPercentage="1"
              :scalesEnabled="false"
              :data="chartsData['unique-stats']['total-vs-unique'].filter(s => s[0] !== 'x')"
            ></analytics-chart>
            <content-loader-placeholder
              v-if="!Boolean(chartsData['unique-stats']['total-vs-unique'].length)"
              :type="'AnalyticsChartLoader'"
              slot="content-loader"
            ></content-loader-placeholder>
          </span>
        </i-box>
      </div>

      <div class="col-lg-3">
        <i-box :contentHeight="235">
          <span slot="title">
            <h5>Total by campaign</h5>
          </span>
          <span slot="content">
            <analytics-chart
              v-if="chartsData['campaign-stats']['points'].length > 1"
              type="doughnut"
              chartStyle="max-height: 200px;"
              :data="chartsData['campaign-stats']['points'].map(s => [s.campaign, s.total])"
            ></analytics-chart>
            <content-loader-placeholder
              v-if="chartsData['campaign-stats']['points'].length <= 1"
              :type="'DoughnutChartLoader'"
              slot="content-loader"
            ></content-loader-placeholder>
          </span>
        </i-box>
      </div>

      <div class="col-lg-3">
        <i-box :contentHeight="235">
          <span slot="title">
            <h5>Total by channel type</h5>
          </span>
          <span slot="content">
            <analytics-chart
              v-if="processChannelStats.length"
              type="polar-area"
              chartStyle="max-height: 200px;"
              :data="processChannelStats"
            ></analytics-chart>
            <content-loader-placeholder
              v-if="!processChannelStats.length"
              :type="'PolarChartLoader'"
              slot="content-loader"
            ></content-loader-placeholder>
          </span>
        </i-box>
      </div>

      <div class="col-lg-3">
        <i-box :contentHeight="235">
          <span slot="title">
            <h5>Heat map</h5>
          </span>
          <span slot="content">
            <analytics-heat-map
              v-if="Boolean([chartsData['heat-map-stats'].points].length)"
              :data="chartsData['heat-map-stats'].points"
              :range="chartsData['heat-map-stats'].range"
              @onChangeDate="$emit('changeDate', $event)"
            ></analytics-heat-map>
            <content-loader-placeholder
              v-if="!Boolean(Object.keys(chartsData['heat-map-stats'].points).length)"
              :type="'AnalyticsHeatMapLoader'"
              slot="content-loader"
            ></content-loader-placeholder>
          </span>
        </i-box>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-12">
        <i-box contentHeight="450">
          <span slot="title">
            <h5>Chart</h5>
            <div class="chart-filters pull-right">
              <label
                class="label label-primary chart-label"
                :class="{ active: selectedFilter === 'timeslot' }"
                @click="$emit('changeFilter', { filter: 'timeslot' })"
              >Timeslots</label>
              <label
                class="label label-primary chart-label"
                :class="{ active: selectedFilter === 'channel' }"
                @click="$emit('changeFilter', { filter: 'channel' })"
              >Channels</label>
              <label
                class="label label-primary chart-label"
                :class="{ active: selectedFilter === 'campaign' }"
                @click="$emit('changeFilter', { filter: 'campaign' })"
              >Campaigns</label>
            </div>
          </span>
          <span slot="content">
            <analytics-time-series-area-chart
              v-if="Boolean(mainChartData.stats.points.length)"
              :data="mainChartData.stats.points"
              :unit="units[units.indexOf(selectedPeriod) - 1]"
              :showLegend="false"
            ></analytics-time-series-area-chart>
            <content-loader-placeholder
              v-if="!Boolean(mainChartData.stats.points.length)"
              :type="'EngagementGraphLoader'"
              slot="content-loader"
            ></content-loader-placeholder>
          </span>
        </i-box>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

import IBox from '@/components/common/IBox';

import AnalyticsUnitBlock from '@/components/analytics/AnalyticsUnitBlock';
import AnalyticsChart from '@/components/analytics/charts/AnalyticsChart';
import AnalyticsHeatMap from '@/components/analytics/charts/AnalyticsHeatMap';
import AnalyticsTimeSeriesAreaChart from '@/components/analytics/charts/AnalyticsTimeSeriesAreaChart';
import ContentLoaderPlaceholder from '@/components/common/ContentLoaderPlaceholder';

export default {
  name: 'EngagementsDashboard',
  components: {
    IBox,

    AnalyticsUnitBlock,
    AnalyticsChart,
    AnalyticsHeatMap,
    AnalyticsTimeSeriesAreaChart,
    ContentLoaderPlaceholder,
  },
  data: () => ({
    units: ['minute', 'hour', 'day', 'month', 'year'],
  }),
  props: {
    selectedDate: {
      type: Object,
      default: () => moment(),
      required: true,
    },
    selectedPeriod: {
      type: String,
      default: 'year',
      required: true,
    },
    selectedFilter: {
      type: String,
      default: 'campaign',
      required: true,
    },
    chartsData: {
      type: Object,
      default: () => ({
        'hour-stats': { total: 0, growth: 0, points: [] },
        'day-stats': { total: 0, growth: 0, points: [] },
        'month-stats': { total: 0, growth: 0, points: [] },
        'year-stats': { total: 0, growth: 0, points: [] },
        'unique-stats': { 'total-vs-unique': [] },
        'campaign-stats': { points: [[]] },
        'channel-type-stats': { points: [] },
        'heat-map-stats': { points: {}, range: [] },
      }),
      required: true,
    },
    mainChartData: {
      type: Object,
      default: () => ({
        stats: { points: [] },
      }),
      required: true,
    },
  },
  computed: {
    processChannelStats() {
      if (!this.chartsData['channel-type-stats'].points.length) {
        return [];
      }
      const points = this.chartsData['channel-type-stats'].points[0];
      return Object.keys(points).map(key => [key, points[key]]);
    },
  },
};
</script>

<style type="text/scss" scoped>
.chart-label {
  opacity: 0.7;
}

.active {
  opacity: 1;
}
</style>



// WEBPACK FOOTER //
// EngagementsDashboard.vue?5555535a